import { alpha } from '@mui/material/styles';
import { blue } from '@mui/material/colors';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS
export const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8)
};

const PRIMARY_LIGHT = {
  lighter: '#dbeafe',
  light: '#93c5fd',
  main: '#3b82f6',
  dark: '#1d4ed8',
  darker: '#1e3a8a',
  contrastText: '#fff'
};

const PRIMARY_DARK = {
  lighter: '#bfdbfe',
  light: '#93c5fd',
  main: '#60a5fa',
  dark: '#3b82f6',
  darker: '#1d4ed8',
  contrastText: '#f8fafc'
};

const SECONDARY_LIGHT = {
  lighter: '#ede9fe',
  light: '#c4b5fd',
  main: '#8b5cf6',
  dark: '#6d28d9',
  darker: '#581c87',
  contrastText: '#fff'
};

const SECONDARY_DARK = {
  lighter: '#ddd6fe',
  light: '#c4b5fd',
  main: '#a78bfa',
  dark: '#8b5cf6',
  darker: '#6d28d9',
  contrastText: '#f8fafc'
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff'
};
const SUCCESS = {
  lighter: '#DCFCE7',
  light: '#A7F3D0',
  main: '#22C55E',
  dark: '#15803D',
  darker: '#14532D',
  contrastText: GREY[900]
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: GREY[800]
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff'
};

const VIOLET = {
  lighter: '#F3E5F5',
  light: '#BA68C8',
  main: '#9C27B0',
  dark: '#7B1FA2',
  darker: '#4A148C',
  contrastText: GREY[800]
};

const METRIC_ACCENTS = {
  success: '#4ade80',
  secondary: '#a78bfa',
  violet: '#a78bfa',
  warning: '#fbbf24',
  error: '#fb7185',
  info: '#38bdf8'
};

const LIVE_BADGE_TOKENS_LIGHT = {
  background: 'rgba(34,197,94,0.18)',
  text: '#22c55e',
  border: 'rgba(34,197,94,0.35)'
};

const LIVE_BADGE_TOKENS_DARK = {
  background: 'rgba(34,197,94,0.12)',
  text: '#4ade80',
  border: 'rgba(34,197,94,0.3)'
};

const TREND_BAR_TOKENS_LIGHT = {
  background: '#fff8e1',
  border: '#fcd34d',
  text: '#b45309'
};

const TREND_BAR_TOKENS_DARK = {
  background: '#3a2e0f',
  border: '#5a4516',
  text: '#fbbf24'
};

const LIGHT_METRIC = {
  background: '#f3f4f6',
  card: '#ffffff',
  border: '#e2e8f0',
  label: '#475569',
  headline: '#0f172a',
  secondary: '#475569',
  shadow: '0 12px 28px rgba(15, 23, 42, 0.1)',
  iconSurface: 'rgba(15,23,42,0.08)'
};

const DARK_METRIC = {
  background: '#0b1220',
  card: '#111c2d',
  border: '#1f2937',
  label: '#94a3b8',
  headline: '#f1f5f9',
  secondary: '#9ca3af',
  shadow: '0 30px 60px rgba(2, 6, 23, 0.55)',
  iconSurface: 'rgba(148,163,184,0.08)'
};

const LOGO_GRADIENT = 'linear-gradient(130deg, #3b82f6 0%, #6366f1 40%, #8b5cf6 100%)';
const LOGO_GRADIENT_SOFT = 'linear-gradient(130deg, rgba(59,130,246,0.2) 0%, rgba(99,102,241,0.2) 50%, rgba(139,92,246,0.2) 100%)';
const LOGO_GRADIENT_SOFT_DARK = 'linear-gradient(130deg, rgba(6,11,25,0.85) 0%, rgba(56,88,150,0.55) 40%, rgba(110,70,182,0.6) 100%)';
const DASHBOARD_CANVAS_SOFT = 'linear-gradient(145deg, rgba(59,130,246,0.08) 0%, rgba(99,102,241,0.08) 50%, rgba(139,92,246,0.1) 100%)';
const DASHBOARD_CANVAS_DARK = 'linear-gradient(145deg, rgba(15,23,42,0.9) 0%, rgba(17,24,39,0.92) 40%, rgba(30,27,75,0.95) 100%)';

const CHART_COLORS = {
  violet: ['#9B63FF', '#B984FF', '#DAB5FF', '#F1E5FF'],
  blue: ['#1643D8', '#3A5EEF', '#6F8CFF', '#A8BBFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE36A', '#FFEF9A', '#FFF6C9', '#FFFBEA'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4']
};

const buildGradients = (isLight) => ({
  primary: createGradient('#60a5fa', '#3b82f6'),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  brand: createGradient('#3b82f6', '#8b5cf6'),
  brandHover: createGradient('#2563eb', '#a855f7'),
  brandSoft: createGradient(alpha('#3b82f6', 0.18), alpha('#8b5cf6', 0.12)),
  logo: LOGO_GRADIENT,
  logoSoft: isLight ? LOGO_GRADIENT_SOFT : LOGO_GRADIENT_SOFT_DARK,
  dashboardCanvas: isLight ? DASHBOARD_CANVAS_SOFT : DASHBOARD_CANVAS_DARK,
  layout: isLight
    ? createGradient('rgba(11,42,134,0.15)', 'rgba(127,75,255,0.12)')
    : createGradient('rgba(3,7,18,0.85)', 'rgba(20,38,78,0.8)')
});

const buildBackground = (isLight) =>
  isLight
    ? { paper: '#FFFFFF', default: '#f8fafc', neutral: GREY[100], elevated: '#FFFFFF' }
    : { paper: '#020617', default: '#0f172a', neutral: '#0b1524', elevated: '#111827' };

const buildText = (isLight) =>
  isLight
    ? { primary: GREY[800], secondary: GREY[600], disabled: GREY[500] }
    : { primary: '#f8fafc', secondary: '#94a3b8', disabled: alpha('#94a3b8', 0.5) };

const buildMetricTokens = (isLight) => ({
  ...(isLight ? LIGHT_METRIC : DARK_METRIC),
  badges: { live: isLight ? LIVE_BADGE_TOKENS_LIGHT : LIVE_BADGE_TOKENS_DARK },
  trend: isLight ? { ...TREND_BAR_TOKENS_LIGHT } : { ...TREND_BAR_TOKENS_DARK },
  accent: { ...METRIC_ACCENTS }
});

const palette = (mode = 'light') => {
  const isLight = mode === 'light';
  const gradients = buildGradients(isLight);

  return {
    mode,
    common: { black: '#000', white: '#fff' },
    primary: isLight ? { ...PRIMARY_LIGHT } : { ...PRIMARY_DARK },
    secondary: isLight ? { ...SECONDARY_LIGHT } : { ...SECONDARY_DARK },
    info: { ...INFO },
    success: { ...SUCCESS },
    warning: { ...WARNING },
    error: { ...ERROR },
    violet: { ...VIOLET },
    grey: GREY,
    gradients,
    chart: CHART_COLORS,
    divider: isLight ? GREY[500_24] : '#1f2937',
    metric: buildMetricTokens(isLight),
    brand: {
      link: isLight ? blue[600] : blue[400],
      linkHover: isLight ? '#1d4ed8' : '#93c5fd'
    },
    text: buildText(isLight),
    background: buildBackground(isLight),
    action: {
      active: isLight ? GREY[600] : GREY[500],
      hover: isLight ? GREY[500_8] : alpha(GREY[500], 0.16),
      selected: isLight ? GREY[500_16] : alpha(GREY[500], 0.24),
      disabled: isLight ? GREY[500_80] : alpha(GREY[500], 0.5),
      disabledBackground: isLight ? GREY[500_24] : alpha(GREY[500], 0.2),
      focus: isLight ? GREY[500_24] : alpha(GREY[500], 0.32),
      hoverOpacity: 0.08,
      disabledOpacity: 0.48
    }
  };
};

export default palette;
